import {AllActivityTypes, AllActivityTypesArray} from "../../types-enums/activity";
import {cloneDeep, fromPairs, get, groupBy, isEqual, sortBy} from "lodash";
import {v4 as uuidv4} from "uuid";
import {isActivityCarBusState, isActivityFlightState, isActivityHotelState} from "../check/type-guards";
import {ActivityFlightState} from "../../types-enums/activity-flight";
import {ActivityHotelState} from "../../types-enums/activity-hotel";
import {groupActivities} from "../check/activities";
import {ActivityCarBusState} from "../../types-enums/activity-car-bus";

/**
 * Updates and sets properties for a flight activity.
 *
 * This function takes an ActivityFlightState object, clones it, and updates its properties.
 * Specifically, it generates a new unique identifier for the flight's `common.id` property
 * and sets the `returnId` field to null. The modified flight object is then returned.
 *
 * @function
 * @param {ActivityFlightState} flight - The flight activity object to be updated.
 * @returns {ActivityFlightState} The updated flight activity object with modified properties.
 */
const setFlightActivityProps = (flight: ActivityFlightState): ActivityFlightState => {
    const updatedFlight = cloneDeep(flight)
    updatedFlight.common.id = uuidv4()
    updatedFlight.returnId = null
    return updatedFlight
}

/**
 * Sets the backend ID for an activity if certain conditions are met.
 *
 * This function assigns the provided backendId to the `backendId` property
 * of the activity object, provided the activity exists and meets the following:
 * - The activity type is grouped as determined by the `groupActivities` function.
 * - The activity is in a valid state*/
const setBackendId = (activity: AllActivityTypes | undefined, backendId: number | null) => {
    if (activity) {
        const {common: {activityType}} = activity
        if (
            groupActivities(activityType) &&
            (isActivityCarBusState(activity) || isActivityHotelState(activity))
        ) activity.backendId = backendId
    }
}

/**
 * Finds the most frequently occurring `backendId` from a provided array of activities.
 *
 * The function processes an array of activities and identifies the `backendId` that appears
 * most frequently, considering only activities that are grouped using `groupActivities`.
 * If multiple `backendId`s (which can be number or null) have the same maximum frequency, `null` is preferred over numerical IDs.
 *
 * @param {AllActivityTypesArray} activities - The array of activity objects to process.
 * @returns {number | null} The most frequent `backendId`. Returns `null` if none is found or if
 *                          `null` has the highest frequency in case of a tie.
 */
const findMostFrequentBackendId = (activities: AllActivityTypesArray): number | null => {
    const frequency = new Map<number | null, number>()
    activities.forEach(activity => {
        const {common: {activityType}} = activity
        if (groupActivities(activityType) && "backendId" in activity) {
            const {backendId} = activity
            frequency.set(backendId, (frequency.get(backendId) || 0) + 1);
        }
    });

    return Array.from(frequency.entries()).reduce((mostFrequent, [id, count]) => {
        const [_, maxCount] = mostFrequent;
        return count > maxCount || (count === maxCount && id === null)
            ? [id, count]
            : mostFrequent
    }, [null, 0] as [number | null, number])[0]
};

/**
 * Identifies and resolves duplicate backend IDs for activities in a given array of activity objects.
 *
 * This function works through the following steps:
 * 1. Filters activities that have a non-null `groupId`.
 * 2. Filters activities with a non-null `backendId` but a null `groupId` and meets specific type criteria.
 * 3. Groups activities by their `groupId` and determines their most frequent `backendId`.
 * 4. Assigns the most frequent `backendId` where applicable to activities grouped by the same `groupId`.
 * 5. Handles cases where activities with a `backendId` but no `groupId` may need their `backendId`
 *    cleared if it conflicts with another activity's `backendId`.
 *
 * The function ensures backend IDs are maintained and assigned correctly across groups
 * and individual activities to prevent conflicts or inconsistencies.
 *
 * @param {AllActivityTypesArray} activities - An array of activities to process.
 *   Each activity is expected to have `common` properties such as `activityType`, `groupId`, and `backendId`.
 *
 * @throws {Error} If any unexpected scenario is encountered or if certain invariants are violated.
 *
 * @returns {void} This function operates as a direct mutation on the input `activities`, modifying
 * activities in place where necessary.
 */
const assignBackendIds = (activities: AllActivityTypesArray): void => {
    const activitiesWithGroupId = activities.filter(activity => activity.common.groupId !== null)
    const activitiesWithBackendIdNotNullAndGroupIdNull = activities.filter(activity => {
        const {common: {activityType, groupId}} = activity
        if (
            groupActivities(activityType) &&
            (isActivityCarBusState(activity) || isActivityHotelState(activity)) &&
            activity.backendId !== null && groupId === null
        ) return true
    })
    const activitiesGroupedByGroupId = groupBy(activitiesWithGroupId, activityWithGroupId => get(activityWithGroupId, "common.groupId"))
    for (const [commonGroupId, activitiesWithCommonGroupId] of Object.entries(activitiesGroupedByGroupId)) {
        const commonBackendId = findMostFrequentBackendId(activitiesWithCommonGroupId)
        activities.forEach(activity => {
            const {common: {activityType, groupId}} = activity
            if (groupId === commonGroupId && (groupActivities(activityType) && "backendId" in activity)) setBackendId(activity, commonBackendId)
        })
    }

    activitiesWithBackendIdNotNullAndGroupIdNull.forEach(activityWithBackendIdNotNullAndGroupIdNull => {
        if (
            groupActivities(activityWithBackendIdNotNullAndGroupIdNull.common.activityType) &&
            (isActivityCarBusState(activityWithBackendIdNotNullAndGroupIdNull) || isActivityHotelState(activityWithBackendIdNotNullAndGroupIdNull))
        ) {
            activities.forEach(activity => {
                if (
                    activity.common.id !== activityWithBackendIdNotNullAndGroupIdNull.common.id &&
                    (groupActivities(activity.common.activityType) && "backendId" in activity) &&
                    activityWithBackendIdNotNullAndGroupIdNull.backendId === activity.backendId
                ) setBackendId(activityWithBackendIdNotNullAndGroupIdNull, null)
            })
        }
    })
}

/**
 * Compares the properties of two `ActivityCarBusState` objects to determine if they are equivalent.
 *
 * This function performs a deep equality check on the `waypoints` property using `isEqual` and also
 * compares other attributes such as `vehicleType`, `fuelType`, `fuelConsumption`, `carCategory`,
 * `manualEnteredDistanceKm`, `calculatedDistanceKm`, `busCategory`, `acrissCode`, and `passengerCount`.
 *
 * @param {ActivityCarBusState} originalCarBus - The first object representing the car or bus activity state.
 * @param {ActivityCarBusState} newCarBus - The second object representing the car or bus activity state.
 * @returns {boolean} Returns `true` if all properties of both objects are equivalent, otherwise `false`.
 */
export const compareCarBusProps = (originalCarBus: ActivityCarBusState, newCarBus: ActivityCarBusState): boolean =>
    isEqual(originalCarBus.waypoints, newCarBus.waypoints) &&
    originalCarBus.vehicleType === newCarBus.vehicleType &&
    originalCarBus.fuelType === newCarBus.fuelType &&
    originalCarBus.fuelConsumption === newCarBus.fuelConsumption &&
    originalCarBus.carCategory === newCarBus.carCategory &&
    originalCarBus.manualEnteredDistanceKm === newCarBus.manualEnteredDistanceKm &&
    originalCarBus.calculatedDistanceKm === newCarBus.calculatedDistanceKm &&
    originalCarBus.busCategory === newCarBus.busCategory &&
    originalCarBus.acrissCode === newCarBus.acrissCode &&
    originalCarBus.passengerCount === newCarBus.passengerCount

/**
 * Compares the properties of two `ActivityHotelState` objects to determine if they are equal.
 *
 * @param {ActivityHotelState} originalHotel - The original hotel state object.
 * @param {ActivityHotelState} newHotel - The new hotel state object to compare against the original.
 * @returns {boolean} Returns `true` if all specified properties in both hotel state objects are equal; otherwise, `false`.
 *
 * The comparison checks include:
 * - Hotel details: name, city, alpha2, alpha3, countryName, stars.
 * - Country and city information for the hotel, including country codes and names.
 * - Hotel-specific attributes: hotel class, guest count, meal plan, and room category.
 */
export const compareHotelProps = (originalHotel: ActivityHotelState, newHotel: ActivityHotelState): boolean =>
    isEqual(originalHotel.hotel?.name, newHotel.hotel?.name) &&
    isEqual(originalHotel.hotel?.city, newHotel.hotel?.city) &&
    isEqual(originalHotel.hotel?.alpha2, newHotel.hotel?.alpha2) &&
    isEqual(originalHotel.hotel?.alpha3, newHotel.hotel?.alpha3) &&
    isEqual(originalHotel.hotel?.countryName, newHotel.hotel?.countryName) &&
    isEqual(originalHotel.hotel?.stars, newHotel.hotel?.stars) &&
    isEqual(originalHotel.country, newHotel.country) &&
    isEqual(originalHotel.city?.name, newHotel.city?.name) &&
    isEqual(originalHotel.city?.alpha2, newHotel.city?.alpha2) &&
    isEqual(originalHotel.city?.alpha3, newHotel.city?.alpha3) &&
    isEqual(originalHotel.city?.countryName, newHotel.city?.countryName) &&
    originalHotel.hotelClass === newHotel.hotelClass &&
    originalHotel.guestCount === newHotel.guestCount &&
    originalHotel.mealPlan === newHotel.mealPlan &&
    originalHotel.roomCategory === newHotel.roomCategory

/**
 * Processes an array of activities, generating unique IDs for each activity
 * and ensuring consistency between related activities such as flights.
 *
 * This function performs the following:
 * - Clones and assigns unique `id` properties to all non-flight activities.
 * - Processes flight activities with `returnId` properties, ensuring proper linkage
 *   between round-trip flights. If a referenced return flight is found, both flights
 *   are cloned, assigned new unique IDs, and linked correctly. Flights without
 *   corresponding `returnId` are simply processed and assigned unique IDs.
 * - Non-flight activities and unlinked flights are included directly in the output
 *   with unique IDs.
 *
 * @param {AllActivityTypesArray} activities - The input array of activities.
 * Each activity will be processed to ensure that it is assigned a unique identifier and properly linked if applicable.
 * @returns {AllActivityTypesArray} A new array of activities with unique IDs
 * and linked relationships where necessary.
 */
const createUniqueActivityIds = (activities: AllActivityTypesArray): AllActivityTypesArray => {
    const flightsWithReturnFlights: ActivityFlightState[][] = []
    const allActivities: AllActivityTypesArray = []
    const flightMap = new Map<string, ActivityFlightState>()
    activities.forEach(activity => isActivityFlightState(activity) && flightMap.set(activity.common.id, activity))
    activities.forEach(activity => {
        if (isActivityFlightState(activity)) {
            const {returnId} = activity
            if (returnId !== null) {
                const referencedFlight = flightMap.get(returnId)
                if (typeof referencedFlight !== 'undefined') {
                    flightsWithReturnFlights.push([cloneDeep(referencedFlight), cloneDeep(activity)])
                } else {
                    allActivities.push(setFlightActivityProps(activity))
                }
            } else {
                allActivities.push(setFlightActivityProps(activity))
            }
        } else {
            const updatedActivity = cloneDeep(activity)
            updatedActivity.common.id = uuidv4()
            allActivities.push(updatedActivity)
        }
    });

    flightsWithReturnFlights.forEach(flights => {
        const referencedFlight = flights[0]
        const returnFlight = flights[1]
        const newReferencedFlightId = uuidv4()
        referencedFlight.common.id = newReferencedFlightId
        returnFlight.returnId = newReferencedFlightId
        returnFlight.common.id = uuidv4()
        allActivities.push(referencedFlight, returnFlight)
    })

    return allActivities
}

/**
 *  When there is the following situation (in this example: All Hotels with the same props (in cases with
 *     useIDsToFindEqual = true, the Semantic IDs are of the Hotels with the same props are equal, in cases with
 *     useIDsToFindEqual = false, the props of the Hotels are equal, but not the Semantic IDs
 *     ))
 *
 *      Day 1  Day 2   Day 3   Day 4   Day 5
 *      A      A       A       A       A
 *      A      A       A               A
 *      A      B       A               A
 *      A              A
 *
 *      Then we must assume that these some of stays should grouped together as one stay
 *
 *      Day 1  Day 2   Day 3   Day 4   Day 5
 *      A------A-------A-------A-------A
 *      A------A-------A               A
 *      A      B       A               A
 *      A              A
 *
 *      while others are separate stays. For that, new ids are assigned to 'group' the stays that belong together.
 *      Hence, a stay that belongs together like
 *
 *     Day 1  Day 2   Day 3   Day 4   Day 5
 *      A------A-------A-------A-------A
 *
 *      will have the same ID through its five elements.
 *
 *
 *      It also updates or removes the backendId from loaded data. Loaded Activities have backendIds assigned to them.
 *      If i.e. a single Activity with a backendId is merged with another group that has a backend ID, the merged
 *      Activities must become the same backendId.
 *      If there are two groups with the same backendId, the backendId of one group must be set to null. If this data
 *      is persited, the backend will delete the "missing" Activities (those Activities whos backendId is null) and create
 *      new entries for the Activities that have null as a backendId.
 */
export const mergeTravelComponentsToJourneyElements = (
    activities: AllActivityTypesArray,
    journeyDaysCount: number,
    assignNewId: boolean,
    useIDsToFindEqual: boolean,
): AllActivityTypesArray => {
    const clonedActivities = cloneDeep(activities)
    const activitiesWithGroupId: AllActivityTypesArray = []
    const activitiesGroupedByDay = groupBy(clonedActivities, item => get(item, 'common.day'))
    const sortedActivitiesGroupedByDay = fromPairs(
        sortBy(Object.keys(activitiesGroupedByDay).map(key => [key, activitiesGroupedByDay[key]]), ([key]) => Number(key))
    )
    for (const [day, activitiesOnCurrentDay] of Object.entries(sortedActivitiesGroupedByDay)) {
        const currentDay = parseInt(day, 10)
        activitiesOnCurrentDay
            .filter(activityOnCurrentDay => {
                if (isActivityFlightState(activityOnCurrentDay)) activitiesWithGroupId.push(activityOnCurrentDay)
                return !activitiesWithGroupId.includes(activityOnCurrentDay) && !isActivityFlightState(activityOnCurrentDay)
            })
            .forEach(activityOnCurrentDay => {
                let nextDay = currentDay + 1
                const {common: {semanticId: activityOnCurrentDaySemanticId, activityType}} = activityOnCurrentDay
                const activityChain = [activityOnCurrentDay]
                for (let i = nextDay; i <= journeyDaysCount; i++) {
                    if (i in activitiesGroupedByDay) {
                        const activityOnNextDay = activitiesGroupedByDay[i]
                            .filter(activityOnNextDay => {
                                if (useIDsToFindEqual) {
                                    if (activityOnNextDay.common.semanticId === activityOnCurrentDaySemanticId && !activitiesWithGroupId.includes(activityOnNextDay)) return true
                                } else {
                                    if (groupActivities(activityType) &&
                                        isActivityHotelState(activityOnCurrentDay) &&
                                        isActivityHotelState(activityOnNextDay) &&
                                        compareHotelProps(activityOnCurrentDay, activityOnNextDay)
                                        && !activitiesWithGroupId.includes(activityOnNextDay)
                                    ) return true
                                    if (groupActivities(activityType) &&
                                        isActivityCarBusState(activityOnCurrentDay) &&
                                        isActivityCarBusState(activityOnNextDay) &&
                                        compareCarBusProps(activityOnCurrentDay, activityOnNextDay)
                                        && !activitiesWithGroupId.includes(activityOnNextDay)
                                    ) return true
                                }
                            })[0]
                        if (typeof activityOnNextDay !== 'undefined') {
                            activityChain.push(activityOnNextDay)
                        } else {
                            break
                        }
                    } else {
                        break
                    }
                }
                if (activityChain.length > 1) {
                    const newGroupId = uuidv4()
                    activityChain.forEach(activity => activity.common.groupId = newGroupId)
                    if (activityChain.length === 2)
                        if (!useIDsToFindEqual) {
                            const newSemanticId = uuidv4()
                            activityChain.forEach(activity => activity.common.semanticId = newSemanticId)
                        }
                } else {
                    if (activityChain.length === 1) activityChain[0].common.groupId = null
                }
                activitiesWithGroupId.push(...activityChain)
            })
    }
    assignBackendIds(activitiesWithGroupId)
    if (!assignNewId) return activitiesWithGroupId
    return createUniqueActivityIds(activitiesWithGroupId)
}